import React, { useEffect, useRef, useState } from "react";
import { RCCIcon, RCCButton, RCCProgress, RCCTooltip, RCCHidden, RCCDropDownItem } from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import formatDate from "src/utils/formatDate";
import formatNumber from "src/utils/formatNumber";
import { useHierarchyOrgChart } from "src/pages/StrategicPlanning/Okr/hierarchyOrgChart";
import { PermissionEnum } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import {IDataSource, IKeyResults, INodeDataSource} from "./interface";
import Photo from "../Photo";
import {Node, optionsStyles, buttonUnderlineStyles, DivKRsSons} from "./styles";
import { TabsKRMODAL } from "./keyResults/interface";
import { TabsOKRMODAL } from "./objectives/interface";
import DropdownWithBackground from "../DropdownWithBackground";
import CheckPermission from "../CheckPermission";

const MyNode: React.FC<INodeDataSource> = ({
	nodeData
}) => {
	const { translate, currentLanguage } = useTranslation();
	const {administrator, user, superUser, checkIfHasSpecificPermission } = useAuth();
	const { REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY } = process.env;
	const { 
		callbackOnAddOKRHierarchy, 
		callbackOnChangeOKRHierarchy, 
		callbackOnDeleteOKRHierarchy, 
		openModalAddOrChangeKr, 
		openModalPerformCheckIn,
		openModalDelete, 
		openModalAddOrChange,
		openModalOKRAttachments,
		openModalOKRComments,
		openModalOKRMissionAndPremises,
		openModalIniciatives 
	} = useHierarchyOrgChart();

	const nodeRef = useRef<HTMLDivElement>(null);
	const krSonsRef = useRef<HTMLDivElement>(null);
	const [OKRSiblingsOpened, setOKRSiblingsOpened] = useState(true);
	const [OKRSonsOpened, setOKRSonsOpened] = useState(true);
	const [hasSiblings, setHasSiblings] = useState(false);
	const [disabled, setDisabled] = useState(false);

	const hasPermissionsToCreateAndEdit = checkIfHasSpecificPermission("okr/panel", PermissionEnum.CREATE_UPDATE);

	const userIsResponsibleKr = (responsible: number) => responsible === user.efa005Id;
	const permisionKr = (userIsResponsible: boolean) => hasPermissionsToCreateAndEdit && ((userIsResponsible || (nodeData?.permissions && nodeData?.permissions > 1)) || administrator || superUser);

	function addKeyResult (data: IDataSource){
		if(!openModalAddOrChangeKr)
			return;

		openModalAddOrChangeKr(undefined, data.id, data.objective, () => callbackOnChangeOKRHierarchy(data.id));
	}

	function addOKRChildren (data: IDataSource){
		if(!openModalAddOrChange)
			return;

		openModalAddOrChange(undefined, data.id, () => callbackOnAddOKRHierarchy(data.id));
	}
    
	function attachmentOkr (okrId: number, okrTitle: string){
		if(!openModalOKRAttachments)
			return;

		openModalOKRAttachments(okrId, okrTitle, () => callbackOnChangeOKRHierarchy(okrId), disabled);
	}

	function changeKr (data: IDataSource, krId: number, disabledX: boolean) {
		if(!openModalAddOrChangeKr)
			return;

		openModalAddOrChangeKr(krId, data.id, data.objective, () => callbackOnChangeOKRHierarchy(data.id), undefined, disabledX);
	}

	function changeOkr (data: IDataSource, tabInit?: number) {
		if(!openModalAddOrChange)
			return;

		openModalAddOrChange(data.id, undefined, () => callbackOnChangeOKRHierarchy(data.id), tabInit);
	}
    
	function checkIn (okrId: number, kr: IKeyResults){
		if(!openModalPerformCheckIn)
			return;

		let measurementForm = "1";
		if(kr.iconMeasurement === "Iniciativas")
			measurementForm = "2";
		else if(kr.iconMeasurement === "Projetos")
			measurementForm = "3";
		else if(kr.iconMeasurement === "Indicadores")
			measurementForm = "4";

		openModalPerformCheckIn(kr.id, kr.title, kr.measurementType.toString(), measurementForm, kr.valueMadeIt, kr.percentage, kr.color, kr.initialValue, kr.goal, () => callbackOnChangeOKRHierarchy(okrId));
	}

	function commentOkr (okrId: number, okrTitle: string){
		if(!openModalOKRComments)
			return;

		openModalOKRComments(okrId, okrTitle, () => callbackOnChangeOKRHierarchy(okrId), disabled);
	}

	function deleteOkr (data: IDataSource) {
		if(!openModalDelete)
			return;

		openModalDelete(data.id , data.objective, () => callbackOnDeleteOKRHierarchy(data.id)); 
	}

	function openCheckInsKR(okr: IDataSource, krId: number, disabledX: boolean){
		if(!openModalAddOrChangeKr)
			return;
		openModalAddOrChangeKr(krId, okr.id, okr.objective, () => callbackOnChangeOKRHierarchy(okr.id), TabsKRMODAL.CHECK_IN, disabledX);
	}

	function openIniciativesModal (krId: number, krTitle: string, okrId: number, userIsResponsible: boolean){
		if(!openModalIniciatives)
			return;

		openModalIniciatives({
			idKr: krId
		}, krTitle, () => callbackOnChangeOKRHierarchy(okrId), permisionKr(userIsResponsible) ? 2 : 1);
	}

	function openMissionAndPromisesModal (okrId: number, okrTitle: string){
		if(!openModalOKRMissionAndPremises)
			return;
		openModalOKRMissionAndPremises(okrId, okrTitle);
	}
	
	function toggleHideSiblings (){
		if(!nodeRef || !nodeRef.current)
			return;

		nodeRef.current.parentElement?.parentElement?.classList.toggle("isSiblingsCollapsed");

		if(OKRSiblingsOpened){
			let prevSiblings = nodeRef.current.parentNode?.parentNode?.previousSibling;
			while(prevSiblings){
				const sibling = prevSiblings as HTMLElement;
				if(!sibling.classList.contains("isSiblingsCollapsed"))
					sibling.classList.add("hidden");
				prevSiblings = prevSiblings.previousSibling;
			}

			let nextSiblings = nodeRef.current.parentNode?.parentNode?.nextSibling;
			while(nextSiblings){
				const sibling = nextSiblings as HTMLElement;
				if(!sibling.classList.contains("isSiblingsCollapsed"))
					sibling.classList.add("hidden");
				nextSiblings = nextSiblings.nextSibling;
			}

			setOKRSiblingsOpened(false);
			return;
		}

		let prevSiblings = nodeRef.current.parentNode?.parentNode?.previousSibling;
		while(prevSiblings){
			const sibling = prevSiblings as HTMLElement;
			sibling.classList.remove("hidden");
			prevSiblings = prevSiblings.previousSibling;
		}

		let nextSiblings = nodeRef.current.parentNode?.parentNode?.nextSibling;
		while(nextSiblings){
			const sibling = nextSiblings as HTMLElement;
			sibling.classList.remove("hidden");
			nextSiblings = nextSiblings.nextSibling;
		}

		setOKRSiblingsOpened(true);
			
	}
	
	function toggleKrSons () {
		if(!krSonsRef || !krSonsRef.current)
			return;

		if(krSonsRef.current.style.display === "none"){
			krSonsRef.current.style.display = "flex";
			return;
		}

		krSonsRef.current.style.display = "none";
		
	}	

	function toggleOKRSons() {
		setTimeout(() => {
			if(!nodeRef || !nodeRef.current)
				return;
			nodeRef.current.parentElement?.classList.toggle("isChildrenCollapsed");
			const ul = nodeRef.current.parentNode?.parentNode?.lastChild as HTMLElement;
			if(OKRSonsOpened){
				ul.classList.add("hidden");
				setOKRSonsOpened(false);
				return;
			}
			ul.classList.remove("hidden");
			setOKRSonsOpened(true);

		}, 200);	
	}

	// Has Siblings
	useEffect(() => {
		setDisabled(!(hasPermissionsToCreateAndEdit && ((nodeData?.permissions && nodeData?.permissions > 1) || administrator || superUser)));

		if(!nodeRef || !nodeRef.current)
			return;
	
		const el = nodeRef.current.parentElement?.parentElement as HTMLElement;
		if(el.previousSibling || el.nextSibling){
			setHasSiblings(true);
		}
	}, []);

	if(!nodeData || !nodeData.id)
		return null;

	return (
		<Node ref={nodeRef}>
			{/* Title */}
        	<div className="title">
        		<span title={nodeData.objective}>{nodeData.objective}</span>
        	</div> 
        
			{/* Options */}
			{!disabled && (
				<div className="controls-container" style={{position: "absolute", top:"2px", right: "2px"}}>
					<DropdownWithBackground
						callerComponent={
							<RCCButton 
								color="transparent"  
								icon={{
									name: "dotsVertical",
									color: "#FFF",
									size: 18
								}}
							/>
						}
						position="leftEnd"
						styleInline={optionsStyles}
					>
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
							<RCCDropDownItem
								selfEnclose
								onClick={() => changeOkr(nodeData)}
							>
								<RCCIcon 
									name="pencil" 
								/>
								{translate("Alterar")}
							</RCCDropDownItem>
						</CheckPermission>
						{(administrator || nodeData.responsibleId === user.efa005Id || nodeData.authorityId === user.efa005Id) &&
							<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
								<RCCDropDownItem 
									selfEnclose
									onClick={() => deleteOkr(nodeData)}
								>
									<RCCIcon 
										name="trash" 
									/>
									{translate("Excluir")}
								</RCCDropDownItem>
							</CheckPermission>
						}
					</DropdownWithBackground>
				</div>
			)}
		

			{/* Content */}
			<div className="content">
				<div className="container-chart-percentage">
					<RCCProgress 
						width="80px" 
						value={nodeData.percentage} 
						color={nodeData.color && nodeData.color !== null && nodeData.color !== "" ? `#${nodeData.color}` : undefined} 
						layout="circle"
						onClick={() => changeOkr(nodeData, TabsOKRMODAL.KEY_RESULTS)} 
					/>
				</div>      

				<div className="container-info-card">
					<div className="info-card-responsible">
						<Photo 
							name={nodeData.responsible} 
							link={nodeData.photo && `${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_usuarios/${nodeData.photo}`}
							hover
						/>
						{nodeData.responsible}
					</div>

					<div className="info-card-buttons">
						<span style={{fontWeight: 600, color: "var(--blue)", fontSize: "11px"}}> <RCCIcon name="calendar" color="var(--blue)" size={12} /> 
							{nodeData.period !== "" && nodeData.period !== null ? nodeData.period : `${formatDate(nodeData.dtStart, currentLanguage)} - ${formatDate(nodeData.dtEnd, currentLanguage)}`}
						</span>
						<button type="button" onClick={() => attachmentOkr(nodeData.id, nodeData.objective)}>
							<RCCIcon name="attachment" color="var(--blue)" size={12} /> 
							{nodeData.attachmentsNumber}
						</button>
						<button type="button" onClick={() => commentOkr(nodeData.id, nodeData.objective)}>
							{nodeData.commentsNumber > 0 ? (
								<RCCIcon name="commentFilled" color="var(--blue)" size={12}/>
							): (
								<RCCIcon name="comment" color="var(--blue)" size={12}/>
							)} 
							{nodeData.commentsNumber}
						</button>
						<RCCTooltip color="blue" text={translate("Missão e Premissa")}>
							<RCCIcon 
								name="exclamation" 
								color="var(--blue)" 
								size={12} 
								onClick={() => openMissionAndPromisesModal(nodeData.id, nodeData.objective)}
								style={{ cursor: "pointer" }}
							/>
						</RCCTooltip>
					</div>

					<div className="info-card-display-number-key-results">
						<RCCTooltip color="grey" text={translate("Visualizar Resultados Chave")} >
							<RCCButton color="grey" onClick={toggleKrSons} icon={{
								name:"key",
								color:"var(--grey-softer)", 
								size: 12
							}}>{nodeData.keyResultsNumber.toString()}</RCCButton> 
						</RCCTooltip>
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
							<RCCTooltip color="grey" text={translate("Novo Resultado Chave")} >
								<RCCButton 
									color="grey" 
									icon={{
										name:"plus", 
										color:"var(--grey-softer)", 
										size: 12
									}} 
									onClick={() => addKeyResult(nodeData)}
									disabled={disabled}
								>
									{translate("Resultado Chave")}
								</RCCButton>
							</RCCTooltip>
						</CheckPermission>
						{nodeData.checkInFrequencyWarning && (
							<div className="indicador-frequencia-checkin" style={{marginLeft: "10px"}}>
								<RCCTooltip color="yellow" text={translate("Existem Check In(s) em atraso, no dia de hoje ou próximo do dia")}>
									<RCCIcon name="exclamationTriangle" color="var(--yellow)" size={12} />
								</RCCTooltip>
							</div>
						)}
					</div>
				</div>

				<div style={{position: "absolute", top: "20px", right: "20px"}}>
					{nodeData.nvl === 1 && (
						<RCCTooltip color="grey" text={translate("Organizacional")}>
							<RCCIcon name="building" color="var(--grey)" size={12}/>
						</RCCTooltip>
					)}
					{nodeData.nvl === 2 && (
						<RCCTooltip color="grey" text={translate("Equipe")}>
							<RCCIcon name="users" color="var(--grey)" size={12}/>
						</RCCTooltip>
					)}
					{nodeData.nvl === 3 && (
						<RCCTooltip color="grey" text={translate("Individual")}>
							<RCCIcon name="user" color="var(--grey)" size={12}/>
						</RCCTooltip>
					)}
				</div>
			</div>

			{/* Expand/Collapse Sons */}
			{nodeData.children && nodeData.children.length > 0 && (
				<div className="openOKRSons" style={{position: "absolute", bottom: "-20px", left: "10px"}}>
					<RCCButton 
						color="transparent"
						onClick={toggleOKRSons}
						icon={!OKRSonsOpened ? {
							name:"eye", 
							color:"var(--blue)", 
							size: 12
						} : {
							name:"eyeSlash", 
							color:"var(--blue)", 
							size: 12
						}}
						styleInline={buttonUnderlineStyles} 
					>
						{!OKRSonsOpened ? (
							<RCCHidden only='xs' >
								{translate("Expandir OKRs Filhos")}
							</RCCHidden>

						): (
							<RCCHidden only='xs' >
								{translate("Ocultar OKRs Filhos")}
							</RCCHidden>
						)}
					</RCCButton>
				</div>       
			)}

			{/* Hide/Unhide Siblings */}
			<div className="focusThisCard" style={hasSiblings ? {display: "flex"} : {display: "none"}}>
				{OKRSiblingsOpened ? (
					<RCCTooltip color="grey" text={translate("Focar nesse card e ocultar seus irmãos")}>
						<RCCIcon name="compress" color="var(--grey-lighter)" size={12} onClick={toggleHideSiblings}/>
					</RCCTooltip>
				):(
					<RCCTooltip color="grey" text={translate("Desfocar desse card e mostrar seus irmãos")}>
						<RCCIcon name="expand" color="var(--grey-lighter)" size={12} onClick={toggleHideSiblings}/>
					</RCCTooltip>
				)}
			</div>

			{/* Add OKR Sons */}
			<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
				<div style={{position: "absolute", bottom: "-20px", right: "10px"}}>
					<RCCButton color="transparent"
						onClick={() => addOKRChildren(nodeData)}
						styleInline={buttonUnderlineStyles}
						icon={{
							name: "plus", 
							color: "var(--blue)", 
							size: 12
						}}
						disabled={disabled}
					>
						<RCCHidden only='xs' >
							{translate("OKR Filho")}
						</RCCHidden>
					</RCCButton>
				</div>
			</CheckPermission>

			{/* Key Results */}
			<DivKRsSons className="keyResultsOfObjectiveInHierarchy_score_okr" ref={krSonsRef} style={{display: "none" }}>
				{nodeData.keyResults && nodeData.keyResults.map((kr) => (
					<div key={`${kr.id}-hierarchy`} className={`content content-key-results ${kr.checkInFrequency === "Hoje" ? "card-border-left-warning" : ""} ${kr.checkInFrequency.substring(0, 8) === "Atrasado" ? "card-border-left-danger" : ""} 	${kr.checkInFrequency.substring(0, 7) === "Próximo" ? "card-border-left-success" : ""}`}>
						<div className="container-info-card">
							<div className="info-card-number-key-results">
								<span className="title-of-kr">{kr.title}</span>
								{kr.description && kr.description !== "" && (
									<RCCTooltip color="grey" text={kr.description}>
										<RCCIcon name="exclamationCircle" size={12} color="var(--grey)"/>
									</RCCTooltip>
								)}
							</div>

							<div className="info-card-responsible" >
								<Photo 
									name={kr.responsible} 
									link={kr.photo && `${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_usuarios/${kr.photo}`}
									hover
									width={30}
									height={30}
								/>
								{kr.responsible}
							</div>

							<div style={{marginLeft: "-15px", display: "flex", alignItems: "center"}}>
								<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
									<RCCTooltip color="grey" text={translate("Realizar Check In")}>
										<RCCButton 
											color="grey" 
											icon={{
												name: "checkSquare", 
												color: "var(--grey-softer)", 
												size: 12
											}} 
											size="xs"
											height={20}
											onClick={() => checkIn(nodeData.id, kr)}
											disabled={!permisionKr(userIsResponsibleKr(kr.responsibleId))}
										>
											{translate("Realizar Check In")}
										</RCCButton>
									</RCCTooltip>
								</CheckPermission>
								<RCCTooltip color="grey" text={`${translate("Iniciativas")}: ${kr.countIniciatives} ${translate("Ações")}: ${kr.countActions}`}>
									<RCCButton 
										color="grey" 
										icon={{
											name: "list", 
											color: "var(--grey-softer)", 
											size: 12
										}}
										size="xs" 
										height={20}
										onClick={() => openIniciativesModal(kr.id, kr.title, nodeData.id, userIsResponsibleKr(kr.responsibleId))}
									>
										{`${translate("Iniciativas")}: ${kr.countIniciatives}`}
									</RCCButton>
								</RCCTooltip>
								{kr.checkInFrequency && kr.checkInFrequency !== null && kr.checkInFrequency !== "" && (
									<>
										{kr.checkInFrequency === "Hoje" && (
											<RCCTooltip
												color="yellow"
												text={translate("Hoje")}
											>
												<RCCIcon name="exclamationCircle" size={12} color="var(--yellow)"/>
											</RCCTooltip>
										)}
										{kr.checkInFrequency.substring(0, 8) === "Atrasado" && (
											<RCCTooltip
												color="red"
												text={translate("Atrasado")}
											>
												<RCCIcon name="exclamationCircle" size={12} color="var(--red)" />
											</RCCTooltip>
										)}
										{kr.checkInFrequency.substring(0, 7) === "Próximo" && (
											<RCCTooltip
												color="yellow"
												text={translate("Próximo")}
											>
											 <RCCIcon name="exclamationCircle" size={12} color="var(--yellow)" />
											</RCCTooltip>
										)}
									</>
								)}
							</div>
						</div>

						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
							<div style={{height: "20px", position: "absolute"}}>
								{kr.iconMeasurement === "Resultados Chave" && (
									<RCCTooltip color="grey" text={`${translate("Forma de Medição")} ${translate("Resultados Chave")}`}>
										<RCCIcon name="key" size={12} color="var(--default-color-font)" onClick={() => changeKr(nodeData, kr.id, !permisionKr(userIsResponsibleKr(kr.responsibleId)))} style={{cursor: "pointer"}}/>
									</RCCTooltip>
								)}
								{kr.iconMeasurement === "Indicadores" && (
									<RCCTooltip color="grey" text={`${translate("Forma de Medição")} ${translate("Indicadores")}`}>
										<RCCIcon name="chartBar" size={12} color="var(--default-color-font)" onClick={() => changeKr(nodeData, kr.id, !permisionKr(userIsResponsibleKr(kr.responsibleId)))} style={{cursor: "pointer"}}/>
									</RCCTooltip>
								)}
								{kr.iconMeasurement === "Projetos" && (
									<RCCTooltip color="grey" text={`${translate("Forma de Medição")} ${translate("Projetos")}`}>
										<RCCIcon name="table" size={12} color="var(--default-color-font)" onClick={() => changeKr(nodeData, kr.id, !permisionKr(userIsResponsibleKr(kr.responsibleId)))} style={{cursor: "pointer"}}/>
									</RCCTooltip>
								)}
								{kr.iconMeasurement === "Iniciativas" && (
									<RCCTooltip color="grey" text={`${translate("Forma de Medição")} ${translate("Iniciativas")}`}>
										<RCCIcon name="list" size={12} color="var(--default-color-font)" onClick={() => changeKr(nodeData, kr.id, !permisionKr(userIsResponsibleKr(kr.responsibleId)))} style={{cursor: "pointer"}}/>
									</RCCTooltip>
								)}
							</div>
						</CheckPermission>

						<div className="container-chart-percentage">
							<RCCTooltip color="grey" text={`${translate("Realizado")} ${formatNumber(kr.valueMadeIt, currentLanguage)}`}>
								<RCCProgress 
									layout="circle" 
									width="75px" 
									value={kr.percentage} 
									color={kr.color && kr.color !== null && kr.color !== "" ? `#${kr.color}` : undefined} 
									onClick={() => openCheckInsKR(nodeData, kr.id, !permisionKr(userIsResponsibleKr(kr.responsibleId)))}
									styleInline=">span { font-size: 0.9rem !important; }"
								/>	
							</RCCTooltip>
						</div>

					</div>
				))}
			</DivKRsSons>
		</Node>
	);
};

export default MyNode;
